import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTable } from "@angular/material/table";
import { ConfirmationDialogComponent, ConfirmationDialogType, GlobalHelpDialogComponent } from "@comunicare/comunicare-dashboard-modals";
import { DataType, IFilter, InfoCommunication, IPatientUser } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { BehaviorSubject, combineLatest } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { HelpData } from "src/app/helpers/helpData";
import { CommunicationsCrudService } from "src/app/providers/communications-crud.service";
import { CommunicationsService } from "src/app/providers/communications.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { PatientService } from "src/app/providers/patient.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { ResponsiveDialogService } from "src/app/providers/responsive-dialog.service";
import { ResponsiveService } from "src/app/providers/responsive.service";
import { SessionService } from "src/app/providers/session.service";
import { UserService } from "src/app/providers/user.service";
import { WidgetBaseComponent } from "../base/widget-base/widget-base.component";
import { Item } from "../item-selector/item-selector.component";
import { WidgetActionConfig } from "../widget-actions/widget-actions.component";
import { PatientCommunicationsListDataSource } from "./patient-communications-list-datasource";

@Component({
  selector: "app-patient-communications",
  templateUrl: "./patient-communications.component.html",
  styleUrls: ["./patient-communications.component.scss", "../base/widget-base/widget-base.component.scss"],
})
export class PatientCommunicationsComponent extends WidgetBaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() set patientUser(pu: IPatientUser) {
    if (pu?.user?.caremateIdentifier) {
      this.pu = pu;
      this.loadDataSource();
    }
  }
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatTable) table: MatTable<InfoCommunication>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("focus") target: ElementRef;
  public displayedColumns: string[] = ["status", "sendDate", "receivedDate", "subject", "action"];
  public dataSource: PatientCommunicationsListDataSource;
  /** Raw active filters, kept in sync to feed `app-item-filter` via the `filterFor` pipe. */
  public activeFilters: IFilter[] = [];
  public sliderData: Item[] = [];
  public showDraft = true;
  public pu: IPatientUser;
  public filterFormTable = this.fb.group({
    fromDate: new UntypedFormControl(moment().subtract(3, "month"), {
      validators: Validators.required,
      updateOn: "blur",
    }),
    toDate: new UntypedFormControl(moment().endOf("day"), {
      validators: Validators.required,
      updateOn: "blur",
    }),
  });
  /**
   * IFilter
   */
  public dataTypeText = DataType.TEXT;
  public dataTypeDate = DataType.DATE;
  public dataTypeChoice = DataType.CHOICE;
  public globalSearchValue = "";
  public loading = true;
  public maxFromDate: string;
  public minToDate: string;
  public today = moment();
  public isAllServices$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private $subRefresh = this.sessionService.refreshCommunicationsList.subscribe(() => {
    if (this.pu) {
      this.loadDataSource();
    }
  });

  /** Subject that emits when the component has been destroyed. */
  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match

  /**
   * shortcuts to get forms value
   */
  private get from() {
    return this.filterFormTable.get("fromDate")?.value as string;
  }
  private get to() {
    return this.filterFormTable.get("toDate")?.value as string;
  }

  public actions: WidgetActionConfig[];

  constructor(
    private preferenceService: PreferenceService,
    public helpData: HelpData,
    private comService: CommunicationsService,
    private patientService: PatientService,
    private crud: CommunicationsCrudService,
    private fb: UntypedFormBuilder,
    private sessionService: SessionService,
    private userService: UserService,
    private healthcareService: HealthcareserviceService,
    private responsiveDialog: ResponsiveDialogService,
    private dialog: MatDialog,
    protected responsiveService: ResponsiveService,
    private translateService: TranslateService
  ) {
    super(responsiveService);
    this.setupServicesWatch();
  }

  ngOnInit(): void {
    this.createDataSource();
    this.filterFormTable.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.updateDate());
  }
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.dataSource.clear();
    this.$subRefresh.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.initPaginatorAndSort();
  }

  private updateDate(): void {
    const from = moment(this.from).format("YYYY-MM-DD");
    const to = moment(this.to).format("YYYY-MM-DD");
    this.maxFromDate = moment(to).toISOString();
    this.minToDate = moment(from).toISOString();
    if (from && to && !this.dataSource.loading) {
      this.dataSource.loadCommunications(this.pu.user.caremateIdentifier, from, to, this.preferenceService.getAutoRefreshWidget());
    }
  }

  private setupServicesWatch(): void {
    if (this.userService.isMonitoringUser) {
      this.setupServices();
      this.sessionService.refreshCurrentMonitService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
      });
    } else {
      this.setupServices();
      this.sessionService.refreshCurrentService.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.setupServices();
      });
    }
  }

  private setupServices(): void {
    if (
      (!this.userService.isMonitoringUser && !this.sessionService.currentService) ||
      (this.userService.isMonitoringUser && !this.sessionService.currentMonitoringService)
    ) {
      this.isAllServices$.next(true);
      return;
    }
    this.isAllServices$.next(this.sessionService.isAllServices(this.userService.isMonitoringUser));
  }

  private createDataSource(): void {
    if (!this.dataSource) {
      this.dataSource = new PatientCommunicationsListDataSource(this.comService);
      this.initActions();
    }
  }

  public initPaginatorAndSort(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  public loadDataSource(): void {
    this.createDataSource();
    this.dataSource.loadCommunications(
      this.pu.user.caremateIdentifier,
      moment(this.from).format("YYYY-MM-DD"),
      moment(this.to).format("YYYY-MM-DD"),
      this.preferenceService.getAutoRefreshWidget()
    );
  }

  /**
   * IFilter
   */
  public getFilter(propertyName: string): IFilter {
    return this.dataSource.getFilter(propertyName);
  }

  public applyFilter(filter: IFilter): void {
    this.dataSource.setFilter(filter);
    this.activeFilters = [...(this.dataSource?.getAllFilters() ?? [])];
  }

  public refreshData(): void {
    this.dataSource.loadCommunications(this.pu.user.caremateIdentifier, this.from, this.to, this.preferenceService.getAutoRefreshWidget());
  }

  public editCommunication(communication: InfoCommunication): void {
    if (!communication.isEditable) {
      return;
    }
    this.crud.editCommunication(communication, null, false, true);
  }

  public deleteCommunication(communication: InfoCommunication): void {
    if (!communication.isEditable) {
      return;
    }
    this.crud.deleteCommunication(communication, this.patientService.toReference([this.pu]));
  }

  public viewCommunication(communication: InfoCommunication): void {
    this.crud.viewCommunication(communication);
  }

  public newCommunication(): void {
    const currentService = this.sessionService.currentService;
    const patientServices = this.pu?.patient?.healthcareservice ?? [];
    const isPatientService = patientServices.some((s) => s.reference === currentService?.reference);

    if (currentService && !isPatientService) {
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            message: this.translateService.instant("communication.notPatientService"),
            type: ConfirmationDialogType.CONFIRM,
          },
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.crud.createCommunication(this.patientService.toReference([this.pu]), null, null, true);
          }
        });
    } else {
      this.crud.createCommunication(this.patientService.toReference([this.pu]), null, null, true);
    }
  }

  public openCommunicationHelp(): void {
    this.responsiveDialog.open(
      GlobalHelpDialogComponent,
      {
        data: { slides: this.helpData.patientCommunicationHelp },
        disableClose: true,
      },
      { maxWidth: "80vw" }
    );
  }

  public onExportCommunications(): void {
    this.comService.exportCommunications(this.pu.user.caremateIdentifier, this.sessionService.userLang);
  }

  public initActions(): void {
    this.actions = [
      {
        type: "button",
        label: "observation.export",
        ariaLabel: "icon button with a download icon",
        icon: "download",
        condition$: combineLatest([this.dataSource.hasData$, this.userService.isAuthorized("dashboard/exportCommsForPatient", "GET")]).pipe(
          map(([hasData, isAuthorized]) => hasData && isAuthorized)
        ),
        action: () => this.onExportCommunications(),
      },
      {
        type: "button",
        label: "communication.newComm",
        ariaLabel: "Add communication",
        icon: "add",
        condition$: this.userService.isAuthorized("dashboard/communication", "POST"),
        conditionDisable$: this.isAllServices$,
        disabledTooltip: "btn.disabledWithAll",
        action: () => this.newCommunication(),
      },
    ];
  }
}
